import { Component, inject, signal } from '@angular/core';

import { Router, RouterModule } from '@angular/router';
import { MenuComponent } from '@design-system/lib/menu/menu/menu.component';
import { MenuDividerComponent } from '@design-system/lib/menu/menu-divider/menu-divider.component';
import { MenuItemComponent } from '@design-system/lib/menu/menu-item/menu-item.component';
import { MenuInteractiveItemComponent } from '@design-system/lib/menu/menu-interactive-item/menu-interactive-item.component';
import { MIXPANEL_EVENTS, pageNameFromUrl } from '@app/core/analytics/mixpanel-events';
import { MixPanelService } from '@app/core/analytics/mixpanel.service';
import { AuthService } from '@app/core/services/auth.service';
import { MockToggleService } from '@app/core/services/mock-toggle.service';
import { environment } from 'src/environments/environment';
import { IconComponent } from '@design-system/lib/icon/icon.component';

interface NavItem {
  label: string;
  route: string;
  editorOnly?: boolean;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    RouterModule,
    MenuComponent,
    MenuDividerComponent,
    MenuItemComponent,
    MenuInteractiveItemComponent,
    IconComponent
],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  authService = inject(AuthService);
  mockToggle = inject(MockToggleService);
  private readonly router = inject(Router);
  private readonly mixPanelService = inject(MixPanelService);
  readonly canUseMockToggle = environment.allowMockToggle;

  menuOpen = signal(false);

  allNavItems: NavItem[] = [
    { label: 'Data Dictionary', route: '/' },
    { label: 'Manage Master Tables', route: '/master-tables', editorOnly: true },
    { label: 'Logs', route: '/logs', editorOnly: true }
  ];

  get navItems(): NavItem[] {
    const isEditor = this.authService.isEditor();
    return this.allNavItems.filter(item => !item.editorOnly || isEditor);
  }

  get userDisplayEmail(): string {
    return this.authService.currentUser()?.email?.trim() ?? '';
  }

  get userDisplayName(): string {
    const authUserName = this.authService.getUserName()?.trim() ?? '';
    const currentUserName = this.authService.currentUser()?.name?.trim() ?? '';
    const fallbackValue = authUserName || currentUserName || this.userDisplayEmail;
    return this.toDisplayName(fallbackValue, this.userDisplayEmail);
  }

  toggleMenu(): void {
    this.menuOpen.update(v => !v);
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }

  navigateTo(route: string): void {
    this.mixPanelService.track(MIXPANEL_EVENTS.HEADER_NAVIGATION, {
      destination: pageNameFromUrl(route),
      previousPage: pageNameFromUrl(this.router.url),
    });
    this.router.navigate([route]);
    this.closeMenu();
  }

  isActive(route: string): boolean {
    if (route === '/') {
      return this.router.url === '/' || this.router.url.startsWith('/dictionary');
    }
    return this.router.url.startsWith(route);
  }

  toggleMock(): void {
    this.mockToggle.toggle();
    window.location.reload();
  }
  logout(): void {
    this.closeMenu();
    // Tracked before logout(): the session sync resets Mixpanel once the user is cleared.
    // Beacon: the logout redirects the page right away.
    this.mixPanelService.track(MIXPANEL_EVENTS.LOGOUT, { section: 'header' }, { beacon: true });
    this.authService.logout();
  }
  portal(): void {
    this.closeMenu();
    this.mixPanelService.track(MIXPANEL_EVENTS.HEADER_NAVIGATION, {
      destination: 'portal',
      previousPage: pageNameFromUrl(this.router.url),
    });
    window.open(environment.portalUrl);
  }

  private toDisplayName(value: string, email: string): string {
    if (!value) {
      return 'User';
    }

    const normalizedValue = value.toLowerCase();
    const normalizedEmail = email.toLowerCase();
    const looksLikeEmail = normalizedValue.includes('@');

    if (!looksLikeEmail && normalizedValue !== normalizedEmail) {
      return value;
    }

    const localPart = value.split('@')[0] ?? '';
    const words = localPart.split(/[._-]+/).filter(Boolean);

    if (words.length === 0) {
      return 'User';
    }

    return words
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}
